import React from "react";
import type { ComponentProps, PartData } from "@enonic/react-components";
import type { HeroButton, HeroProps } from "./Hero.d";

type OverlayColor = "dark" | "light" | "none";

function overlayToRgba(color: OverlayColor, opacityPercent: number): string {
  const opacity = Math.max(0, Math.min(100, opacityPercent)) / 100;
  if (color === "none" || opacity === 0) return "transparent";
  const rgb = color === "dark" ? "0, 0, 0" : "255, 255, 255";
  return `rgba(${rgb}, ${opacity})`;
}

function minHeightToStyle(minHeight: HeroProps["minHeight"]): React.CSSProperties {
  switch (minHeight) {
    case "medium":
      return { minHeight: "28rem" };
    case "small":
    default:
      return { minHeight: "18rem" };
  }
}

export const HeroPart = (props: ComponentProps<PartData>) => {
  const data = (props.data as Partial<HeroProps>) ?? {};

  const {
    superTitle = "",
    title = "",
    subTitle = "",
    alignment = "center",
    minHeight = "small",
    backgroundImageUrl,
    imageOpacity = 0,
    overlayColor = "none",
    textColor = "dark",
    buttonStyle = "button",
    pillColor = "light",
    buttons = [],
  } = data;

  const hasButtons = Array.isArray(buttons) && buttons.length > 0;
  const hasContent = Boolean(superTitle || title || subTitle) || hasButtons;

  if (!hasContent && !backgroundImageUrl) {
    return <p>Add hero content!</p>;
  }

  const contentAlignClass = alignment === "left" ? "text-left" : "text-center";
  const contentTextClass = textColor === "light" ? "text-light" : "text-dark";

  const sectionStyle: React.CSSProperties = {
    position: "relative",
    maxWidth: "calc(100vw + 2.5rem)",
    margin: "0 0 0 0",
    ...(backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {}),
    ...minHeightToStyle(minHeight),
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    backgroundColor: overlayToRgba(overlayColor, imageOpacity),
  };

  const contentStyle: React.CSSProperties = {
    position: "relative",
    paddingTop: "3rem",
    paddingBottom: "3rem",
  };

  return (
    <section className="hero-outer" style={sectionStyle} aria-label={title || "Hero"}>
      <div style={overlayStyle} aria-hidden="true" />
      <div className="hero-section" style={contentStyle}>
        <div className={[contentAlignClass, contentTextClass].filter(Boolean).join(" ")}>
          {superTitle && <p className="text-smallcaps">{superTitle}</p>}
          {title && <h1 className="text-hero">{title}</h1>}
          {subTitle && <p className="text-large">{subTitle}</p>}

          {hasButtons && (
  <div className="button-group">
    {(buttons as HeroButton[]).map((btn, idx) => {
      const external = !!btn?.url?.match(/^https?:/);
      return (
        <a
          key={idx}
          className={`btn${buttonStyle === "pill" ? ` btn--pill${pillColor === "dark" ? " btn--pill--dark" : ""}` : ` ${btn.backgroundColor ?? ""}`}${btn.showArrow ? " btn--has-arrow" : ""}`}          href={btn?.url}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
        >
          {btn.label}
          {btn.showArrow && (
            <span className="btn__arrow" aria-hidden="true">→</span>
          )}
        </a>
      );
    })}
  </div>
)}  
        </div>
      </div>
    </section>
  );
};

export default HeroPart;
